import {
  Box,
  Button,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Modal,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef } from "@mui/x-data-grid";
import Chip from "components/Chip";
import SearchBar from "components/SearchBar";
import TableComponent from "components/TableComponent";
import { Colors } from "constants/colors";
import MainContainer from "layout/MainContainer";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommonBody, Listing } from "types/General";
import { isValidInput } from "utils/validations";
import EditText from "components/editText";
import {
  useAddAgreementMutation,
  useLazyDeleteAgreementQuery,
  useLazyGetAgreementListQuery,
  useLazyGetAllInvestmentQuery,
  useUpdateAgreementMutation,
} from "service/investment";
import { Loader, showError, showToast } from "helpers";
import { UploadMedia, Uploadpdf } from "utils/mediaUpload";
import { generateEncryptedKeyBody } from "utils/crypto";
import { ConfirmationModal } from "Modals/confirmationModal";
import CustomPagination from "components/Panigation";

const ManageAgreement = () => {
  const navigate = useNavigate();

  const label = { inputProps: { "aria-label": "Switch demo" } };
  const [getAgreementList] = useLazyGetAgreementListQuery();
  const [addAgreement] = useAddAgreementMutation();
  const [getInvestmentPlans] = useLazyGetAllInvestmentQuery();
  const [deleteAgreement] = useLazyDeleteAgreementQuery();
  const [updateAgreement] = useUpdateAgreementMutation();

  const [agreementData, setAgreementData] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [name, setName] = useState("");
  const [investmentData, setInvestmentData] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [isEdit, setIsEdit] = useState(false);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLoading(true);
    const file = event.target.files?.[0];
    if (!file) return setLoading(false);
    try {
      const res = await Uploadpdf(file);
      const imageUrl = res?.data?.image;
      if (imageUrl) {
        setPdfUrl(imageUrl);
        showToast("File uploaded successfully.");
      }
    } catch {
      showError("Error uploading file.");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!name) {
      showError("Please enter name");
      return;
    }

    if (!selectedPlan) {
      showError("Please select investment plan");
      return;
    }

    if (!pdfUrl) {
      showError("Please upload pdf");
      return;
    }

    try {
      setLoading(true);
      const body = {
        name: name,
        investmentPlan: selectedPlan,
        content: pdfUrl,
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      let response;

      if (isEdit) {
        response = await updateAgreement({
          body: encryptedBody,
          id: selectedId,
        }).unwrap();
      } else {
        response = await addAgreement({ body: encryptedBody }).unwrap();
      }

      setLoading(false);
      if (response.statusCode === 200) {
        setOpen(false);
        showToast(response?.message || "");
        getAgreementListApi();
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleStatusToggle = async (id: string, status: boolean) => {
    const body = {
      isActive: status,
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body);
      if (!encryptedBody) {
        showError("Encryption failed. Please try again.");
        return;
      }
      const response = await updateAgreement({
        body: encryptedBody as CommonBody,
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        const updatePlan = agreementData?.map((item: any) => {
          if (item?._id === id) {
            return {
              ...item,
              isActive: status,
            };
          }
          return item;
        });
        setAgreementData(updatePlan);
        showToast(
          status ? "Activated successfully" : "Deactivated successfully"
        );
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const columns: GridColDef[] = [
    {
      flex: 1,
      minWidth: 100,
      field: "name",
      headerName: "Name",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.name || "-"}</p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 100,
      field: "plan",
      headerName: "Investment Plan",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.planDetails?.name || "-"}</p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 150,
      field: "status",
      filterable: false,
      sortable: false,
      headerName: "Status",
      renderCell: ({ row }) => {
        return (
          <Switch
            {...label}
            size="small"
            checked={row?.isActive}
            onChange={() => handleStatusToggle(row?._id, !row?.isActive)}
          />
        );
      },
    },

    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="table_actions v2">
          <Box>
            <IconButton>
              <img src="/static/images/eye_icon.svg" alt="" />
            </IconButton>
          </Box>
          <Box>
            <IconButton
              onClick={() => {
                setName(row?.name);
                setPdfUrl(row?.content);
                setSelectedPlan(row?.planDetails?._id);
                setOpen(true);
                setIsEdit(true);
                setSelectedId(row?._id);
              }}
            >
              <img src="/static/images/edit2_icon.svg" alt="" />
            </IconButton>
          </Box>
          <Box>
            <IconButton
              onClick={() => {
                setSelectedId(row?._id);
                setDeleteOpen(true);
              }}
            >
              <img src="/static/images/trash2_icon.svg" alt="" />
            </IconButton>
          </Box>
        </div>
      ),
    },
  ];

  const deleteAgreementApi = async (id: string) => {
    setLoading(true);
    try {
      const response = await deleteAgreement({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        getAgreementListApi();
        showToast(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  const getAgreementListApi = async () => {
    setLoading(true);
    try {
      const response = await getAgreementList({
        page: 1,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setAgreementData(response?.data?.list || []);
        setCount(response?.data?.pagination?.total);
        setPage(response?.data?.pagination?.page);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getInvestmentApi = async () => {
    setLoading(true);
    try {
      const response = await getInvestmentPlans({
        page: 1,
        search: "",
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setInvestmentData(response?.data?.list || []);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getInvestmentApi();
  }, []);

  useEffect(() => {
    getAgreementListApi();
  }, [debouncedSearchTerm, page]);

  return (
    <MainContainer>
      <Loader isLoading={loading} />
      <div className="main_title">
        <h1>Agreement Management</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
          Agreement Management
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Box>
                <Button
                  className="btn btn_orange"
                  onClick={() => {
                    setOpen(true);
                    setIsEdit(false);
                    setPdfUrl("");
                  }}
                >
                  Add Template
                </Button>
              </Box>
            </div>
          </div>
          {/* 
          <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="basic tabs example"
            className="custom_tabs_links"
          >
            <Tab label="Templates" {...a11yProps(0)} />
            <Tab label="Signed Agreements" {...a11yProps(1)} />
          </Tabs> */}

          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={agreementData}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />
            {investmentData?.length ? (
              <CustomPagination
                currentPage={page - 1}
                totalPages={Math.ceil(count / 10)}
                count={count}
                pageSize={10}
                onPageChange={(page) => setPage(page + 1)}
              />
            ) : null}
          </div>
        </Box>
      </div>

      <Modal
        className="modal addNotification_modal"
        id="addNotificationModal"
        aria-labelledby="addNotification-modal-title"
        aria-describedby="addNotification-modal-description"
        open={open}
        onClose={() => setOpen(false)}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
            <div className="modal_title">
              <h2>Add Agreement Template</h2>
            </div>
            <form className="form" action="">
              <div className="gap_p">
                <div className="control_group w_50">
                  <label>Template Name</label>
                  <TextField
                    variant="outlined"
                    placeholder="Template Name"
                    fullWidth
                    hiddenLabel
                    value={name}
                    onChange={(e) => {
                      if (e.target.value === " " || e.target.value === ".") {
                      } else {
                        setName(e.target.value);
                      }
                    }}
                    inputProps={{
                      maxLength: 40,
                      "aria-label": "Category Name",
                    }}
                  />
                </div>
                <div className="control_group w_50">
                  <label>Investment Plan</label>
                  <Select
                    fullWidth
                    value={selectedPlan}
                    displayEmpty
                    onChange={(value) => setSelectedPlan(value.target.value)}
                    label="Select User"
                    // disabled={selectAll}
                  >
                    <MenuItem value="" disabled>
                      Select Plan
                    </MenuItem>
                    {investmentData?.map((plan: any) => (
                      <MenuItem key={plan?._id} value={plan?._id}>
                        {plan?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                {/* 
                <div className="control_group w_100">
                  <label>Agreement Content</label>
                  <EditText content={content} setContent={setContent} />
                </div> */}

                <div className="control_group w_33">
                  <label>Plan Document (PDF)</label>

                  {pdfUrl ? (
                    <Button
                      variant="outlined"
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener"
                      sx={{ mt: 2 }}
                    >
                      Download PDF
                    </Button>
                  ) : (
                    <div
                      style={{
                        position: "relative",
                        width: 400,
                        height: 200,
                        minWidth: 120,
                        minHeight: 120,
                        border: "1.5px solid #ccc",
                        borderRadius: 8,
                        background: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {/* PDF Preview Icon */}

                      <label
                        htmlFor="pdf-upload"
                        style={{
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                          borderRadius: "50%",
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          border: "2px solid #fff",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          zIndex: 2,
                        }}
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="pdf-upload"
                          type="file"
                          inputProps={{ accept: "application/pdf" }}
                          onChange={handleImageUpload}
                        />

                        <img
                          src={"/static/images/add_image.svg"}
                          alt="pdf preview"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "contain",
                            opacity: pdfUrl ? 1 : 0.5,
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="control_group">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    color="primary"
                  />
                }
                label="Select All"
              />
            </div> */}
              <div className="footer_btn_flex">
                <Button
                  className="btn btn_highlight"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button className="btn btn_secondary" onClick={handleSubmit}>
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        text={"Are you sure to delete this Agreement"}
        openModal={deleteOpen}
        setOpenModal={setDeleteOpen}
        handleSubmit={() => deleteAgreementApi(selectedId)}
      />
    </MainContainer>
  );
};

export default ManageAgreement;
